import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import type { TFunction } from 'i18next';
import { type ResultData, ResultTemplateKey, SUGGESTION_FIELD } from 'types/result';
import { getSectionManualNote, stripBoldMarkdown } from 'utils';
import { htmlToPlainText } from 'utils/richText';
import { ANALYTICS_EVENTS } from 'analytics/events';
import type { useAnalytics } from 'analytics';
import type { EditorAutosaveDeps } from 'hooks/useAutosaveStatus';

export interface UseResultSectionsEditorProps extends EditorAutosaveDeps {
  result: ResultData | undefined;
  trackEvent: ReturnType<typeof useAnalytics>['trackEvent'];
  t: TFunction;
}

export interface UseResultSectionsEditorReturn {
  editMode: { [key: string]: boolean };
  editedText: { [key: string]: string };
  toggleEditMode: (key: string) => void;
  handleTextChange: (key: string, value: string) => void;
  handleCopy: (key: string) => void;
  saveEditsBeforeExport: (extraFields?: Record<string, unknown>) => Promise<void>;
  flushPendingEdits: () => Promise<Record<string, string>>;
  adoptSuggestionSections: (fresh: ResultData, sentText: Record<string, string>) => void;
}

export function useResultSectionsEditor({
  result,
  autosaveEditAsync,
  autosaveTimeouts,
  setAutoStatus,
  setHasUserEdits,
  trackEvent,
  t,
}: UseResultSectionsEditorProps): UseResultSectionsEditorReturn {
  const [editMode, setEditMode] = useState<{ [key: string]: boolean }>({});
  const [editedText, setEditedText] = useState<{
    [key: string]: string;
  }>({});

  const resultIdRef = useRef(result?.id);
  resultIdRef.current = result?.id;

  useEffect(() => {
    setEditMode({});
    setEditedText({});
  }, [result?.id]);

  const takePendingEdits = useCallback((): Record<string, string> => {
    const pending: Record<string, string> = {};
    Object.keys(editedText).forEach((key) => {
      if (!autosaveTimeouts.current[key]) return;
      clearTimeout(autosaveTimeouts.current[key]);
      delete autosaveTimeouts.current[key];
      pending[key] = editedText[key];
    });
    return pending;
  }, [editedText, autosaveTimeouts]);

  const adoptSuggestionSections = useCallback(
    (fresh: ResultData, sentText: Record<string, string>) => {
      if (fresh.id !== resultIdRef.current) return;
      setEditedText((prev) => {
        const next = { ...prev };
        (Object.keys(SUGGESTION_FIELD) as ResultTemplateKey[]).forEach((key) => {
          if (prev[key] === sentText[key]) next[key] = getSectionManualNote(key, fresh);
        });
        return next;
      });
    },
    []
  );

  const toggleEditMode = useCallback(
    (key: string) => {
      setEditMode((prev) => ({ ...prev, [key]: !prev[key] }));
      clearTimeout(autosaveTimeouts.current[key]);
      setAutoStatus((prev) => ({ ...prev, [key]: 'typing' }));
      if (!editMode[key]) {
        const html = getSectionManualNote(key as ResultTemplateKey, result).replace('<empty>', '');
        setEditedText((prev) => (key in prev ? prev : { ...prev, [key]: html }));
      }
    },
    [editMode, result, autosaveTimeouts, setAutoStatus]
  );

  const handleTextChange = useCallback(
    (key: string, value: string) => {
      setEditedText((prev) => ({ ...prev, [key]: value }));
      setHasUserEdits(true);
      setAutoStatus((prev) => ({ ...prev, [key]: 'typing' }));
      clearTimeout(autosaveTimeouts.current[key]);

      autosaveTimeouts.current[key] = setTimeout(() => {
        setAutoStatus((prev) => ({ ...prev, [key]: 'saving' }));
        const fieldData = { [key]: value } as Record<string, string>;
        void autosaveEditAsync(fieldData)
          .then(() => setAutoStatus((prev) => ({ ...prev, [key]: 'saved' })))
          .catch(() => setAutoStatus((prev) => ({ ...prev, [key]: 'error' })));
      }, 1000);
    },
    [autosaveTimeouts, autosaveEditAsync, setAutoStatus, setHasUserEdits]
  );

  const saveEditsBeforeExport = useCallback(
    async (extraFields: Record<string, unknown> = {}): Promise<void> => {
      const pending = takePendingEdits();
      const payload = { ...pending, ...extraFields };
      if (Object.keys(payload).length === 0) return;
      try {
        await autosaveEditAsync(payload);
      } catch (error) {
        Object.entries(pending).forEach(([key, value]) => {
          if (!autosaveTimeouts.current[key]) handleTextChange(key, value);
        });
        throw error;
      }
    },
    [takePendingEdits, autosaveEditAsync, autosaveTimeouts, handleTextChange]
  );

  const flushPendingEdits = useCallback(async (): Promise<Record<string, string>> => {
    await saveEditsBeforeExport();
    return editedText;
  }, [saveEditsBeforeExport, editedText]);

  const handleCopy = useCallback(
    (key: string) => {
      trackEvent(ANALYTICS_EVENTS.RESULT_SECTION_COPIED, {
        section: key,
      });
      const textToCopy = editedText[key] || getSectionManualNote(key as ResultTemplateKey, result);
      const cleanText = stripBoldMarkdown(htmlToPlainText(textToCopy));
      void navigator.clipboard.writeText(cleanText).then(() => {
        toast.success(t('result.textCopied'));
      });
    },
    [editedText, result, trackEvent, t]
  );

  return useMemo(
    () => ({
      editMode,
      editedText,
      toggleEditMode,
      handleTextChange,
      handleCopy,
      saveEditsBeforeExport,
      flushPendingEdits,
      adoptSuggestionSections,
    }),
    [
      editMode,
      editedText,
      toggleEditMode,
      handleTextChange,
      handleCopy,
      saveEditsBeforeExport,
      flushPendingEdits,
      adoptSuggestionSections,
    ]
  );
}
